<template>
  <div
    class="card mb-25 border-0 rounded-0 bg-white to-do-list-box letter-spacing"
  >
    <div
      class="card-body pt-0 pb-15 pb-sm-20 pb-md-25 pb-lg-30 ps-15 pe-15 ps-sm-20 pe-sm-20 ps-md-25 pe-md-25 ps-lg-30 pe-lg-30"
    >
      <!-- ================= CIN ================= -->

      <div
        class="card-head bg-white d-md-flex align-items-center justify-content-between pt-15 pb-15 ps-15 pe-15 ps-sm-20 pe-sm-20 ps-md-25 pe-md-25 ps-lg-30 pe-lg-30"
      >
        <h5 class="card-title fw-bold mb-0">Pièce d'Identité (C.I.N)</h5>
      </div>

      <!-- UPLOAD -->
      <div class="row" v-if="!user.ask_cin_verified">
        <p class="text-sm text-slate-500 mb-4">
          Téléchargez le recto et verso de votre carte nationale.
        </p>

        <div class="col-12">
          <!-- RECTO -->
          <label class="upload-box">
            <input
              type="file"
              hidden
              accept="image/*,application/pdf"
              @change="onCinRectoChange"
            />
            <div class="upload-content">
              <div :class="iconClass(cinRecto)">RECTO</div>
              <div>
                <p class="title">
                  {{ cinRecto?.name ?? "Charger le recto" }}
                </p>
                <p class="hint">JPG, PNG, PDF</p>
              </div>
            </div>
          </label>
        </div>
        <div class="col-12">
          <!-- VERSO -->
          <label class="upload-box mt-4">
            <input
              type="file"
              hidden
              accept="image/*,application/pdf"
              @change="onCinVersoChange"
            />
            <div class="upload-content">
              <div :class="iconClass(cinVerso)">VERSO</div>
              <div>
                <p class="title">
                  {{ cinVerso?.name ?? "Charger le verso" }}
                </p>
                <p class="hint">JPG, PNG, PDF</p>
              </div>
            </div>
          </label>
        </div>
      </div>

      <!-- STATUS -->
      <template v-else>
        <div class="status">
          <template v-if="user.cin_verified === 0">
            <span class="pending">Vérification en cours</span>
          </template>

          <template v-else-if="user.cin_verified === 1">
            <span class="success">Identité approuvée</span>
          </template>

          <template v-else>
            <span class="error">Document refusé</span>
            <p class="reason">{{ user.raison_cin_verified }}</p>
            <button class="retry" @click="resetCin">Réessayer</button>
          </template>
        </div>
      </template>

      <!-- ACTION -->
      <div v-if="!user.ask_cin_verified" class="p-6 bg-slate-50 border-t">
        <button
          :disabled="!cinRecto || !cinVerso || loading"
          @click="sendCin"
          :class="buttonClass(!cinRecto || !cinVerso)"
        >
          {{ loading ? "Traitement..." : "Soumettre Identité" }}
        </button>
      </div>
    </div>
  </div>
  <div
    class="card mb-25 border-0 rounded-0 bg-white to-do-list-box letter-spacing"
  >
    <!-- ================= CONTRAT ================= -->
    <div
      class="card-body pt-0 pb-15 pb-sm-20 pb-md-25 pb-lg-30 ps-15 pe-15 ps-sm-20 pe-sm-20 ps-md-25 pe-md-25 ps-lg-30 pe-lg-30"
    >
      <div
        class="card-head bg-white d-md-flex align-items-center justify-content-between pt-15 pb-15 ps-15 pe-15 ps-sm-20 pe-sm-20 ps-md-25 pe-md-25 ps-lg-30 pe-lg-30"
      >
        <h5 class="card-title fw-bold mb-0">Contrat de Service</h5>
      </div>

      <!-- UPLOAD -->
      <template v-if="!user.ask_cntr_verified">
        <label class="upload-box large">
          <input
            type="file"
            hidden
            accept="image/*,application/pdf"
            @change="onContratChange"
          />
          <div v-if="!contratFile">
            <p class="title">Charger le contrat signé</p>
            <p class="hint">Cliquez ou glissez le fichier</p>
          </div>
          <div v-else>
            <p class="success">✓ {{ contratFile.name }}</p>
            <button class="retry mt-2" @click.prevent="clearContrat">
              Changer
            </button>
          </div>
        </label>
      </template>

      <!-- STATUS -->
      <template v-else>
        <div class="status">
          <template v-if="user.contrat_verified === 0">
            <span class="pending">Analyse du contrat</span>
          </template>

          <template v-else-if="user.contrat_verified === 1">
            <span class="success ">Contrat validé</span>
          </template>

          <template v-else>
            <span class="error">Contrat rejeté</span>
            <p class="reason">{{ user.raison_cntr_verified }}</p>
            <button class="retry btn btn-primary" @click="resetContrat">Renvoyer</button>
          </template>
        </div>
      </template>
    </div>

    <!-- ACTION -->
    <div v-if="!user.ask_cntr_verified" class="p-6 bg-slate-50 border-t">
      <button
        :disabled="!contratFile || loading"
        @click="sendContrat"
        :class="buttonClass(!contratFile)"
      >
        {{ loading ? "Soumission..." : "Envoyer le Contrat" }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import api from '@/utils/axios'
import { ref, reactive, onMounted } from 'vue'

/* ================= TYPES ================= */

interface UserVerification {
  ask_cin_verified: boolean
  ask_cntr_verified: boolean
  cin_verified: number
  contrat_verified: number
  raison_cin_verified?: string
  raison_cntr_verified?: string
}

/* ================= STATE ================= */

const user = reactive<UserVerification>({
  ask_cin_verified: false,
  ask_cntr_verified: false,
  cin_verified: 0,
  contrat_verified: 0
})

const cinRecto = ref<File | null>(null)
const cinVerso = ref<File | null>(null)
const contratFile = ref<File | null>(null)
const loading = ref(false)

/* ================= API ================= */

const loadStatus = async (): Promise<void> => {
  try {
    const { data } = await api.get<UserVerification>(
      '/api/auth/verification/status'
    )

    Object.assign(user, data)
  } catch (error) {
    console.error('Failed to load verification status', error)
  }
}

/* ================= ACTIONS ================= */

const sendCin = async (): Promise<void> => {
  if (!cinRecto.value || !cinVerso.value) {
    alert('Veuillez sélectionner les deux fichiers CIN')
    return
  }

  const formData = new FormData()
  formData.append('cin_recto', cinRecto.value)
  formData.append('cin_verso', cinVerso.value)

  try {
    loading.value = true

    await api.post('/api/auth/verification/cin', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    cinRecto.value = null
    cinVerso.value = null

    await loadStatus()
  } catch (error) {
    console.error('CIN upload failed', error)
    alert('Erreur lors de l’envoi du CIN')
  } finally {
    loading.value = false
  }
}

const sendContrat = async (): Promise<void> => {
  if (!contratFile.value) {
    alert('Veuillez sélectionner un fichier contrat')
    return
  }

  const formData = new FormData()
  formData.append('contrat', contratFile.value)

  try {
    loading.value = true

    await api.post('/api/auth/verification/contrat', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    contratFile.value = null

    await loadStatus()
  } catch (error) {
    console.error('Contrat upload failed', error)
    alert('Erreur lors de l’envoi du contrat')
  } finally {
    loading.value = false
  }
}

/* ================= HELPERS ================= */

const resetCin = (): void => {
  user.ask_cin_verified = false
  cinRecto.value = null
  cinVerso.value = null
}

const resetContrat = (): void => {
  user.ask_cntr_verified = false
  contratFile.value = null
}

const onCinRectoChange = (e: Event): void => {
  cinRecto.value = (e.target as HTMLInputElement).files?.[0] || null
}

const onCinVersoChange = (e: Event): void => {
  cinVerso.value = (e.target as HTMLInputElement).files?.[0] || null
}

const onContratChange = (e: Event): void => {
  contratFile.value = (e.target as HTMLInputElement).files?.[0] || null
}

const clearContrat = (): void => {
  contratFile.value = null
}

/* ================= LIFECYCLE ================= */

const iconClass = (file: File | null) =>
  file ? 'icon success-bg' : 'icon'

  const buttonClass = (disabled: boolean) =>
  disabled
    ? 'btn disabled'
    : 'btn active'
onMounted(loadStatus)
</script>


<style scoped>
.upload-box {
  border: 2px dashed #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  cursor: pointer;
  transition: 0.2s;
}
.upload-box:hover {
  background: #f9fafb;
}
.upload-box.large {
  padding: 40px;
  text-align: center;
}
.upload-content {
  display: flex;
  gap: 16px;
  align-items: center;
}
.icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #64748b;
}
.success-bg {
  background: #dcfce7;
  color: #16a34a;
}
.title {
  font-weight: 600;
}
.hint {
  font-size: 12px;
  color: #94a3b8;
}
.status {
  text-align: center;
  padding: 30px 0;
}
.pending {
  color: #2563eb;
  font-weight: bold;
}
.success {
  color: #16a34a;
  font-weight: bold;
}
.error {
  color: #dc2626;
  font-weight: bold;
}
.reason {
  font-size: 12px;
  margin-top: 6px;
}
.retry {
  font-size: 12px;
  color: #fff;
  margin-top: 10px;
}
.btn {
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  font-weight: bold;
  transition: 0.2s;
}
.btn.active {
  background: #2563eb;
  color: white;
}
.btn.disabled {
  background: #e5e7eb;
  color: #94a3b8;
}
</style>
